import React, { useEffect, useState } from 'react';
import { searchDrugProducts } from '../services/drugMonographService';

const STATUS_OPTIONS = [
  { value: 'current', label: 'Current' },
  { value: 'past', label: 'Past' },
];

function shouldSearchDrugProducts(query) {
  const trimmed = String(query || '').trim();
  return Boolean(trimmed) && (trimmed.length >= 2 || /^\d{8}$/.test(trimmed));
}

export default function MedicationForm({ onClose, onSave, saving }) {
  const [form, setForm] = useState({
    medicationName: '',
    productName: '',
    din: '',
    activeIngredientGroupNumber: '',
    dosage: '',
    frequency: '',
    instructions: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: '',
    status: 'current',
    reminderEnabled: false,
    reminderTimes: ['08:00'],
  });
  const [drugQuery, setDrugQuery] = useState('');
  const [drugResults, setDrugResults] = useState([]);
  const [drugSearchLoading, setDrugSearchLoading] = useState(false);
  const [drugSearchError, setDrugSearchError] = useState('');
  const [error, setError] = useState('');

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    let active = true;
    const trimmedQuery = drugQuery.trim();

    if (form.din && trimmedQuery === form.productName) {
      setDrugResults([]);
      setDrugSearchLoading(false);
      setDrugSearchError('');
      return () => {
        active = false;
      };
    }

    if (!shouldSearchDrugProducts(trimmedQuery)) {
      setDrugResults([]);
      setDrugSearchLoading(false);
      setDrugSearchError('');
      return () => {
        active = false;
      };
    }

    setDrugSearchLoading(true);
    setDrugSearchError('');

    const timer = setTimeout(async () => {
      try {
        const results = await searchDrugProducts(trimmedQuery);
        if (active) {
          setDrugResults(Array.isArray(results) ? results : []);
        }
      } catch (searchError) {
        if (active) {
          setDrugResults([]);
          setDrugSearchError(searchError?.message || 'Unable to search drug products right now.');
        }
      } finally {
        if (active) {
          setDrugSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [drugQuery, form.din, form.productName]);

  const handleDrugQueryChange = (value) => {
    setDrugQuery(value);
    setForm((prev) => ({
      ...prev,
      medicationName: value,
      productName: '',
      din: '',
      activeIngredientGroupNumber: '',
    }));
  };

  const handleDrugSelect = (drug) => {
    const productName = String(drug?.productName || '').trim();
    const din = String(drug?.din || '').trim();
    const activeIngredientGroupNumber = String(drug?.activeIngredientGroupNumber || '').trim();

    setDrugQuery(productName);
    setDrugResults([]);
    setDrugSearchError('');
    setError('');
    setForm((prev) => ({
      ...prev,
      medicationName: productName,
      productName,
      din,
      activeIngredientGroupNumber,
    }));
  };

  const handleSave = () => {
    if (!form.din || !form.productName) {
      setError('Please choose a medication from the product name or DIN search results.');
      return;
    }

    if (!form.dosage.trim() || !form.frequency.trim() || !form.startDate) {
      setError('Dosage, frequency, and start date are required.');
      return;
    }
    if (form.status === 'past' && !form.endDate) {
      setError('End date is required for past medications.');
      return;
    }

    setError('');
    onSave({
      medicationName: form.productName.trim(),
      productName: form.productName.trim(),
      din: form.din.trim(),
      activeIngredientGroupNumber: form.activeIngredientGroupNumber.trim(),
      dosage: form.dosage.trim(),
      frequency: form.frequency.trim(),
      instructions: form.instructions.trim(),
      startDate: form.startDate,
      endDate: form.endDate || null,
      status: form.status,
      reminderEnabled: Boolean(form.reminderEnabled),
      reminderTimes: form.reminderEnabled
        ? form.reminderTimes.map((t) => String(t).trim()).filter(Boolean)
        : [],
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '14px',
        zIndex: 1000,
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '560px',
          maxHeight: '92vh',
          overflowY: 'auto',
          borderRadius: '14px',
          background: 'white',
          padding: '22px',
          boxShadow: '0 18px 48px rgba(2, 6, 23, 0.25)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Add Medication</h3>
        <p style={{ margin: '6px 0 16px', fontSize: '13px', color: '#6b7280' }}>
          Search by product name or DIN so the medication is linked to the patient and ready for monograph preloading.
        </p>

        {error && (
          <div
            style={{
              marginBottom: '12px',
              padding: '10px 12px',
              background: '#FCEBEB',
              border: '1px solid #FECACA',
              borderRadius: '8px',
              color: '#991B1B',
              fontSize: '13px',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Medication product name or DIN</label>
            <input
              value={drugQuery}
              onChange={(event) => handleDrugQueryChange(event.target.value)}
              placeholder="Start typing a product name or 8-digit DIN"
              style={inputStyle}
            />

            {drugSearchError && (
              <div style={inlineErrorStyle}>
                {drugSearchError}
              </div>
            )}

            <div
              style={{
                marginTop: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {drugSearchLoading ? (
                <div style={searchStateStyle}>Searching drug products...</div>
              ) : drugResults.length > 0 ? (
                drugResults.map((drug) => (
                  <button
                    key={`${drug.din}-${drug.productName}`}
                    type="button"
                    onClick={() => handleDrugSelect(drug)}
                    style={{
                      ...searchResultButtonStyle,
                      borderColor: form.din === drug.din ? 'var(--teal)' : '#dbe4f0',
                      background: form.din === drug.din ? '#effcf9' : 'white',
                    }}
                  >
                    <div style={{ fontWeight: '700', color: '#0f172a', textAlign: 'left' }}>{drug.productName}</div>
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#475569', textAlign: 'left' }}>
                      DIN {drug.din}
                    </div>
                    <div style={{ marginTop: '3px', fontSize: '11px', color: '#64748b', textAlign: 'left' }}>
                      Active ingredient group {drug.activeIngredientGroupNumber || 'N/A'}
                    </div>
                  </button>
                ))
              ) : shouldSearchDrugProducts(drugQuery) ? (
                <div style={searchStateStyle}>No matching drug products found.</div>
              ) : (
                <div style={searchStateStyle}>Select one of the matching products to continue.</div>
              )}
            </div>
          </div>

          {form.din && form.productName && (
            <div style={selectedDrugCardStyle}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{form.productName}</div>
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#475569' }}>DIN {form.din}</div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <input
              value={form.dosage}
              onChange={(event) => update('dosage', event.target.value)}
              placeholder="Dosage (e.g. 5 mg)"
              style={inputStyle}
            />
            <input
              value={form.frequency}
              onChange={(event) => update('frequency', event.target.value)}
              placeholder="Frequency (e.g. once daily)"
              style={inputStyle}
            />
          </div>

          <textarea
            value={form.instructions}
            onChange={(event) => update('instructions', event.target.value)}
            placeholder="Instructions (optional)"
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={labelStyle}>Start date</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(event) => update('startDate', event.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>End date (optional)</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(event) => update('endDate', event.target.value)}
                style={inputStyle}
                disabled={form.status !== 'past'}
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Status</label>
            <select
              value={form.status}
              onChange={(event) => {
                const nextStatus = event.target.value;
                update('status', nextStatus);
                if (nextStatus !== 'past') update('endDate', '');
              }}
              style={inputStyle}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
            <input
              type="checkbox"
              checked={form.reminderEnabled}
              onChange={(event) => update('reminderEnabled', event.target.checked)}
            />
            Enable reminder schedule (for future notifications)
          </label>

          {form.reminderEnabled && (
            <div>
              <label style={labelStyle}>Reminder time</label>
              <input
                type="time"
                value={form.reminderTimes[0] || '08:00'}
                onChange={(event) => update('reminderTimes', [event.target.value])}
                style={inputStyle}
              />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
          <button onClick={onClose} style={secondaryBtn} type="button">Cancel</button>
          <button onClick={handleSave} style={primaryBtn} disabled={saving} type="button">
            {saving ? 'Saving...' : 'Save Medication'}
          </button>
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '12px',
  color: '#6b7280',
  fontWeight: '600',
};

const inputStyle = {
  width: '100%',
  padding: '11px 12px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
  fontFamily: 'inherit',
  background: 'white',
  boxSizing: 'border-box',
};

const inlineErrorStyle = {
  marginTop: '10px',
  padding: '9px 11px',
  background: '#FCEBEB',
  border: '1px solid #FECACA',
  borderRadius: '8px',
  color: '#991B1B',
  fontSize: '12px',
};

const searchStateStyle = {
  border: '1px dashed #cbd5e1',
  borderRadius: '12px',
  background: '#f8fafc',
  color: '#64748b',
  fontSize: '12px',
  padding: '12px',
};

const searchResultButtonStyle = {
  border: '1px solid #dbe4f0',
  borderRadius: '12px',
  padding: '12px',
  background: 'white',
  cursor: 'pointer',
};

const selectedDrugCardStyle = {
  border: '1px solid #bae6fd',
  background: 'linear-gradient(135deg, #f0fdfa 0%, #eff6ff 100%)',
  borderRadius: '12px',
  padding: '12px',
};

const secondaryBtn = {
  flex: 1,
  padding: '11px 12px',
  borderRadius: '8px',
  border: '1px solid var(--teal)',
  color: 'var(--teal)',
  background: 'white',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
};

const primaryBtn = {
  flex: 1,
  padding: '11px 12px',
  borderRadius: '8px',
  border: 'none',
  color: 'white',
  background: 'var(--teal)',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
};
